import {
	TGetApplicantAssignmentAutocompleteResponse,
	TGetEnablePublicApplicantsResponse,
	TGetFranchiseAssignmentAutocompleteResponse,
	TGetPublicAssignmentsResponse,
	TGetPublicListWithDateResponse,
} from '@/services';
import {
	convertGenderCodeToName,
	formatDate,
	getNoNumber,
	showDataGridWithSymbol,
} from '@/utils';

import { pick } from 'ramda';

import { TApplicantStringDetails } from './pa2-01-assignment-calendar.type';

export const getApplicantHistoriesGrade = (grade: string) => {
	return grade
		? grade === 'BASIC_GRADE'
			? '기본 등급'
			: `${grade?.replace('GRADE_', '')}등급`
		: '';
};

const assignmentFormat = (
	data: TGetPublicListWithDateResponse['assignments'][number]
) => {
	let applicantHistories = (
		data?.applicantHistories ? JSON.parse(data?.applicantHistories) : []
	) as TApplicantStringDetails[];

	if (applicantHistories.length) {
		applicantHistories = applicantHistories.map((item) => ({
			...item,
			grade: getApplicantHistoriesGrade(item?.grade),
		}));
	}

	const timeFormat = {
		inputFormat: 'HH:mm:ss',
		outputFormat: 'HH:mm',
	};

	return {
		id: data.id,
		title: data.employerName,
		allDay: true,
		start: data.day,
		end: data.day,
		resource: {
			...pick(['franchiseId', 'name', 'memo', 'relaxTime'], data),
			franchiseRequestNum: data.requestNum,
			assignmentNum: data.assignmentNum,
			productionTypeDetail: data?.productionTypeDetail || '-',
			startTime: data?.startTime ? formatDate(data?.startTime, timeFormat) : '',
			endTime: data?.endTime ? formatDate(data?.endTime, timeFormat) : '',
			applicantHistories,
			publicFranchiseId: data.publicFranchiseId,
		},
	};
};

export const formatPublicListWithDate = (
	data: TGetPublicListWithDateResponse
) => {
	const assignments =
		data?.assignments?.map((assignment) => assignmentFormat(assignment)) || [];

	// Sum assigned workers per day and map to { day, assignmentNum }
	const assignedByDay = (() => {
		const grouped: Record<string, number> = {};
		assignments?.forEach((a) => {
			const day = a?.start;
			const num = a?.resource?.assignmentNum || 0;
			if (!day || num <= 0) return;
			grouped[day] = (grouped[day] || 0) + num;
		});
		return Object.entries(grouped).map(([day, assignmentNum]) => ({
			day,
			assignmentNum,
		}));
	})();

	return {
		...pick(['statistical', 'workProgress', 'totalPublicApplicantCnt'], data),
		assignmentsWithDate: assignments,
		assignedByDay,
		totalAssignmentApplicant:
			data?.statistical?.statistical?.totalAssignmentApplicant || 0,
	};
};

export const formatPublicAssignments = (
	data: TGetPublicAssignmentsResponse
) => {
	return data;
};

export const formatEnablePublicApplicants = (
	data: TGetEnablePublicApplicantsResponse
) => {
	const content = data?.content?.map((applicant, index) => ({
		...applicant,
		id: applicant?.applicantId,
		no: getNoNumber(data, index),
		entryDepartureDate: showDataGridWithSymbol(
			formatDate(applicant?.entryDate),
			formatDate(applicant?.departureDate)
		),
		workingDayLast30Days: applicant?.workingDayLast30Days
			? `${applicant?.workingDayLast30Days}일`
			: '-',
		gender: convertGenderCodeToName(applicant?.gender),
	}));

	return {
		total: data?.total,
		page: data?.page,
		pageSize: data?.pageSize,
		content,
	};
};

export const formatApplicantAssignmentAutocomplete = (
	data: TGetApplicantAssignmentAutocompleteResponse
) => {
	return data.map((item) => ({
		value: item.id,
		label: item.name,
	}));
};

export const formatFranchiseAssignmentAutocomplete = (
	data: TGetFranchiseAssignmentAutocompleteResponse
) => {
	return data.map((item) => ({
		value: item.id,
		label: item.name,
	}));
};
